<template>
  <RouterView />
</template>

<script setup>
import { watch } from 'vue'
import { RouterView } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useConversationStore } from '@/stores/conversation'
import { useIMStore } from '@/stores/im'
import { useGlobalStore } from '@/stores/global'
import { dbManager } from '@/indexdb/chat'
import { appInitManager } from '@/managers/AppInitManager'
import { clearAllVerifiers, clearAllStates, clearAllNonces } from '@/utils/oauth'
import { useMatchPopVisibilityReport } from '@/composables/useMatchPopVisibilityReport'

const userStore = useUserStore()
const conversationStore = useConversationStore()
const imStore = useIMStore()
const globalStore = useGlobalStore()

useMatchPopVisibilityReport()

/**
 * 应用初始化流程（登录后执行）
 * 使用 AppInitManager 管理初始化流程
 * 只有活跃标签页才能执行
 */
async function handleUserLogin() {
  const userId = userStore.getId()
  const memberId = userStore.getMemberId()

  if (!userId) {
    console.warn('[App] User ID not found, skipping initialization')
    return
  }
  try {
    // 使用初始化管理器执行流程
    await appInitManager.initialize({
      userId,
      memberId
    })
  } catch (error) {
    console.error('[App] Failed to initialize app:', error)
  }
}

/**
 * 用户登出清理
 * 集中处理所有退出登录后的清理工作
 */
function handleUserLogout() {
  console.log('[App] User logged out, cleaning up...')

  // 1. 销毁 IM 连接
  imStore.destroyIM()

  // 2. 清空会话列表状态
  conversationStore.clearConversations()

  // 3. 清除 OAuth 状态
  clearAllVerifiers()
  clearAllStates()
  clearAllNonces()

  // 4. 关闭数据库连接
  dbManager.closeDatabase()

  // 5. 重置初始化管理器状态
  appInitManager.reset()

  console.log('[App] Cleanup completed')
}

// 监听登录状态变化
watch(
  () => [userStore.isLoggedIn, globalStore.isActiveTab],
  ([isLoggedIn, isActiveTab], [prevIsLoggedIn] = []) => {
    // 只有是登录状态且是活跃标签页时才执行登录流程
    if (isLoggedIn && isActiveTab) {
      handleUserLogin()
    }
    // 只有是从登录状态变为未登录状态时才清理
    if (!isLoggedIn && prevIsLoggedIn) {
      handleUserLogout()
    }
  },
  { immediate: true, deep: true } // 立即执行一次，深度监听
)
</script>

<style lang="less">
// 全局样式保留</style>
